/**
 * Report the browser's own unhandled errors to the app (round 179, BACKLOG A.8).
 *
 * A.8's note on this lane was exact: *"the Lit islands have none of this; `tests/ui/` gates zero
 * console errors in CI and production reports nothing."* An error thrown inside an island's `render()`
 * blanks a panel and writes to a console nobody is looking at.
 *
 * ⚠ **It is deliberately not a `try/catch` anywhere near a page.** Both hooks are on `window`, so a
 * throw from any island, any component and any awaited action is covered without a single call site -
 * and there is nothing for a future page to forget to do.
 *
 * ⚠ **`sendBeacon`, not `api()`.** Three reasons and they all matter here. It survives the page being
 * navigated away from or closed, which is exactly what a person does when the UI breaks; it cannot
 * throw, so the reporter can never become a second error inside the handler for the first; and it does
 * not go through `api()`, whose armed-401 path would try to send a broken page to the sign-in screen
 * over a diagnostic. The fallback is a `fetch` with `keepalive` for the one browser without it.
 *
 * ⚠ **Deduplicated in the page, and capped.** A render loop can throw sixty times a second; the server
 * groups by fingerprint so the TABLE is safe either way, but the network is not. One report per distinct
 * message per page load, and a hard ceiling after which this stops sending entirely.
 *
 * ⚠ **The message travels, the file paths travel, and nothing else does.** No DOM, no state, no page
 * URL beyond its first segment (the server truncates that too - a hash route carries release ids). This
 * is the same rule the server lanes follow: capture nothing a person typed.
 */

const ENDPOINT = "/api/client-errors";
/** One page load may report this many distinct errors, then it stops. A loop is one bug, not forty. */
const MAX_REPORTS = 5;

const seen = new Set<string>();
let sent = 0;

function report(message: string, stack: string, page: string): void {
	if (sent >= MAX_REPORTS) return;
	const key = `${message}|${stack.split("\n")[1] ?? ""}`;
	if (seen.has(key)) return;
	seen.add(key);
	sent++;

	const body = JSON.stringify({ message, stack, page });
	try {
		if (typeof navigator.sendBeacon === "function") {
			// A Blob with the JSON content type, because a bare string posts as text/plain and the
			// app-wide JSON parser would hand the route an empty body.
			navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
			return;
		}
	} catch {
		// sendBeacon throws on some content types in some engines - fall through to fetch.
	}
	// `keepalive` is what lets this outlive the page, like sendBeacon. A rejection is swallowed: there
	// is no possible useful response to failing to report a failure, and re-throwing here would turn
	// the reporter into a second unhandled rejection, which this module is the handler for.
	void fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {
		// intentionally ignored - see above
	});
}

/** Install the two window hooks. Called once from `app.ts` boot, before the first navigation. */
export function initErrorReporting(): void {
	window.addEventListener("error", event => {
		// `event.error` is the throw; `message` is all there is for a cross-origin script error, where
		// the browser deliberately withholds the rest. Reporting the little it gives is still worth it:
		// "Script error." from a page tells us a third party broke, which is otherwise invisible.
		const err = event.error as Error | undefined;
		report(err?.message ?? event.message ?? "unknown error", err?.stack ?? "", location.pathname);
	});

	window.addEventListener("unhandledrejection", event => {
		const reason = event.reason as unknown;
		const err = reason instanceof Error ? reason : undefined;
		report(err?.message ?? String(reason), err?.stack ?? "", location.pathname);
	});
}
